/* A dead-simple, 4-zone component playground. Pick any component (zone 1), see
 * it drawn by the REAL renderer (zone 2), flip its closed-set options (zone 3),
 * and watch the A2UI JSON that produces it update live (zone 4). No code, no
 * jargon — just click and see. One component at a time, built via the same
 * buildPreviewTree the screen composer uses (so it supplies sample children). */
import { useMemo, useState } from "react";
import { CATALOG, GROUPS } from "../content/catalog";
import { DEFAULTS } from "../content/samples";
import { buildPreviewTree } from "../preview/buildTree";
import { SurfaceStage } from "../preview/SurfaceStage";
import { ControlsPanel } from "../playground/ControlsPanel";
import { JsonPanel } from "../playground/JsonPanel";
import type { Component } from "../preview/engine/types";

const groupLabel: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em",
  color: "var(--gd-muted)", margin: "0 0 6px",
};
const groupBlurb: React.CSSProperties = {
  fontSize: 11.5, fontWeight: 400, textTransform: "none", letterSpacing: 0,
  color: "var(--gd-muted)", marginLeft: 6, opacity: 0.85,
};
const pickRow: React.CSSProperties = {
  cursor: "pointer", fontFamily: "var(--gd-mono)", fontSize: 13,
};

function freshValues(type: string): Record<string, unknown> {
  return { ...(DEFAULTS[type] ?? {}) };
}

export function ComponentsPlayground() {
  const [type, setType] = useState<string>("Button");
  const [values, setValues] = useState<Record<string, unknown>>(() => freshValues("Button"));

  function pick(next: string) {
    if (next === type) return;
    setType(next);
    setValues(freshValues(next));
  }
  function updateValue(name: string, value: unknown) {
    setValues((prev) => ({ ...prev, [name]: value }));
  }

  const def = CATALOG.components[type];

  const tree = useMemo(
    () => buildPreviewTree(type, def, values),
    [type, def, values],
  );

  // The single component node that the renderer draws — its componentProperties
  // is the real A2UI for this one component, updating live as options change.
  const rootNode: Component | undefined = tree.components.find((c) => c.id === tree.rootId);
  const nodeJson = rootNode ? rootNode.componentProperties : {};

  return (
    <div className="gd-section">
      <div className="gd-section-intro">
        <h2>Components playground</h2>
        <p>
          Components are the building blocks of every screen. Pick one on the left and it draws
          instantly in the live preview — exactly what your users see. Change its options and watch
          the preview and the A2UI JSON update together. Nothing to install, nothing to write.
        </p>
      </div>

      <div className="gd-z3">
        {/* ZONE 1 — pick a component */}
        <div className="gd-zone">
          <div className="gd-col-head">Component</div>
          <div className="gd-zone-body gd-zone-pick">
            {GROUPS.map((g) => (
              <div key={g.id}>
                <div style={groupLabel} title={g.blurb}>
                  {g.label}
                  <span style={groupBlurb}>{g.blurb}</span>
                </div>
                <div className="gd-rowlist">
                  {g.members.map((m) => (
                    <div
                      key={m}
                      className={"gd-listrow" + (m === type ? " sel" : "")}
                      style={pickRow}
                      onClick={() => pick(m)}
                      title={CATALOG.components[m]?.description ?? m}
                    >
                      {m}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ZONE 2 — live preview (real renderer) */}
        <div className="gd-zone">
          <div className="gd-col-head">Live preview · {type}</div>
          <div className="gd-zone-body gd-zone-stage" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <SurfaceStage components={tree.components} rootId={tree.rootId} />
          </div>
        </div>

        {/* RIGHT column — options (top) + the A2UI JSON it produces (below) */}
        <div className="gd-z3-col">
          {/* options — closed-set controls drive the preview */}
          <div className="gd-zone">
            <div className="gd-col-head">Options</div>
            <div className="gd-zone-body">
              <p className="gd-ctl-type" style={{ margin: "0 0 12px", lineHeight: 1.5 }}>
                {def?.description ?? "Adjust this component’s options — every change is live."}
              </p>
              <ControlsPanel def={def} values={values} onChange={updateValue} />
            </div>
          </div>

          {/* the A2UI JSON this component produces */}
          <div className="gd-zone">
            <JsonPanel value={nodeJson} title={`A2UI JSON · ${type}`} />
          </div>
        </div>
      </div>
    </div>
  );
}
